import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, CheckCircle, Clock, Download, AlertCircle,
  Truck, Receipt, Building2, Phone, Mail, MapPin, Camera, X, Image, FileUp, File
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ValidationPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [status, setStatus] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [deliveryPhoto, setDeliveryPhoto] = useState(null);
  const [deliveryTicket, setDeliveryTicket] = useState(null);
  const [ticketFileName, setTicketFileName] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const ticketInputRef = useRef(null);

  useEffect(() => {
    fetchDocument();
  }, [token]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/validate/${token}`);
      setTransaction(response.data.transaction);
      setStatus(response.data.status);
      if (response.data.status === 'already_validated') {
        setValidated(true);
      }
      // Load existing delivery photo if any
      if (response.data.transaction?.delivery_photo) {
        setDeliveryPhoto(response.data.transaction.delivery_photo);
      }
      // Load existing delivery ticket if any
      if (response.data.transaction?.delivery_ticket) {
        setDeliveryTicket(response.data.transaction.delivery_ticket);
        setTicketFileName(response.data.transaction.delivery_ticket_name || 'ticket.pdf');
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      setError(err.response?.data?.detail || 'Document non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setShowCamera(true);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      // Fallback to file input
      fileInputRef.current?.click();
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const photoData = canvas.toDataURL('image/jpeg', 0.8);
      setDeliveryPhoto(photoData);
      stopCamera();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDeliveryPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTicketSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTicketFileName(file.name);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDeliveryTicket(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleValidate = async () => {
    try {
      setValidating(true);
      
      // For BL, upload photo and ticket first
      if (transaction?.type === 'bl') {
        if (deliveryPhoto || deliveryTicket) {
          await axios.post(`${API}/validate/${token}/attachments`, { 
            photo: deliveryPhoto,
            ticket: deliveryTicket,
            ticket_name: ticketFileName
          });
        }
      }
      
      await axios.post(`${API}/validate/${token}`);
      setValidated(true);
      setStatus('already_validated');
      await fetchDocument();
    } catch (err) {
      console.error('Error validating:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/validate/${token}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const typeLabels = { devis: 'Devis', bl: 'BL', facture: 'Facture' };
      link.setAttribute('download', `AGRABAT_${typeLabels[transaction?.type] || 'Doc'}_${transaction?.reference || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const handleDownloadDeliveryReport = async () => {
    try {
      const response = await axios.get(`${API}/validate/${token}/delivery-report`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGRABAT_Rapport_Livraison_${transaction?.reference || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading delivery report:', err);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'devis': return <FileText className="w-8 h-8" />;
      case 'bl': return <Truck className="w-8 h-8" />;
      case 'facture': return <Receipt className="w-8 h-8" />;
      default: return <FileText className="w-8 h-8" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = { devis: 'Devis', bl: 'Bon de Livraison', facture: 'Facture' };
    return labels[type] || 'Document';
  };

  const isBL = transaction?.type === 'bl';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0C1B38] mx-auto"></div>
          <p className="mt-4 text-slate-600">Chargement du document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Document non trouvé</h1>
          <p className="text-slate-600">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0C1B38] text-[#F8D500] mb-4">
            {getTypeIcon(transaction?.type)}
          </div>
          <h1 className="text-3xl font-bold text-[#0C1B38]">AGRABAT</h1>
          <p className="text-slate-600 mt-1">
            {isBL ? 'Confirmation de livraison' : 'Validation de document'}
          </p>
        </div>

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black z-50 flex flex-col">
            <div className="flex justify-between items-center p-4 bg-black/80">
              <span className="text-white font-semibold">Prendre une photo</span>
              <Button variant="ghost" onClick={stopCamera} className="text-white">
                <X className="w-6 h-6" />
              </Button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <video ref={videoRef} autoPlay playsInline className="max-h-full max-w-full" />
            </div>
            <div className="p-6 bg-black/80 flex justify-center">
              <Button 
                onClick={capturePhoto} 
                className="w-16 h-16 rounded-full bg-white hover:bg-gray-200"
              >
                <Camera className="w-8 h-8 text-black" />
              </Button>
            </div>
            <canvas ref={canvasRef} className="hidden" />
          </div>
        )}

        {/* Hidden file inputs */}
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleFileSelect} 
          accept="image/*" 
          capture="environment"
          className="hidden" 
        />
        <input 
          type="file" 
          ref={ticketInputRef} 
          onChange={handleTicketSelect} 
          accept="image/*,.pdf" 
          className="hidden" 
        />

        {/* Document Card */}
        <Card className="overflow-hidden shadow-xl">
          {/* Status Banner */}
          <div className={`p-4 ${validated ? 'bg-green-500' : 'bg-[#F8D500]'} text-center`}>
            <div className="flex items-center justify-center gap-2">
              {validated ? (
                <>
                  <CheckCircle className="w-6 h-6 text-white" />
                  <span className="text-white font-bold text-lg">
                    {isBL ? 'LIVRAISON CONFIRMÉE' : 'DOCUMENT VALIDÉ'}
                  </span>
                </>
              ) : (
                <>
                  <Clock className="w-6 h-6 text-[#0C1B38]" />
                  <span className="text-[#0C1B38] font-bold text-lg">
                    {isBL ? 'EN ATTENTE DE CONFIRMATION' : 'EN ATTENTE DE VALIDATION'}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Document Info */}
          <div className="p-6">
            {/* Reference and Type */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-6 border-b">
              <div>
                <Badge className="bg-[#0C1B38] text-white mb-2">{getTypeLabel(transaction?.type)}</Badge>
                <h2 className="text-2xl font-bold text-[#0C1B38]">{transaction?.reference}</h2>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Date</p>
                <p className="font-semibold">
                  {new Date(transaction?.created_at).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>

            {/* Client Info */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Client
              </h3>
              <div className="space-y-2">
                <p className="font-bold text-lg">{transaction?.client_name}</p>
                {transaction?.client_address && (
                  <p className="text-slate-600 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> {transaction.client_address}
                  </p>
                )}
                {transaction?.client_phone && (
                  <p className="text-slate-600 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> {transaction.client_phone}
                  </p>
                )}
                {transaction?.client_email && (
                  <p className="text-slate-600 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> {transaction.client_email}
                  </p>
                )}
              </div>
            </div>

            {/* Items - Different display for BL */}
            <div className="mb-6">
              <h3 className="font-semibold text-slate-700 mb-3">
                {isBL ? 'Articles à livrer' : 'Articles'}
              </h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-slate-600">Description</th>
                      <th className="text-center p-3 text-sm font-semibold text-slate-600">
                        {isBL ? 'Qté à livrer' : 'Qté'}
                      </th>
                      {!isBL && (
                        <>
                          <th className="text-right p-3 text-sm font-semibold text-slate-600">Prix unit.</th>
                          <th className="text-right p-3 text-sm font-semibold text-slate-600">Total</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {transaction?.items?.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-3">{item.description || item.product_type}</td>
                        <td className="p-3 text-center font-bold">{item.quantity} {item.unit}</td>
                        {!isBL && (
                          <>
                            <td className="p-3 text-right">{item.unit_price?.toLocaleString('fr-FR')} FCFA</td>
                            <td className="p-3 text-right font-semibold">{item.total?.toLocaleString('fr-FR')} FCFA</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Total - Only for non-BL */}
            {!isBL && (
              <div className="bg-[#0C1B38] text-white rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg">Total TTC</span>
                  <span className="text-2xl font-bold text-[#F8D500]">
                    {transaction?.total?.toLocaleString('fr-FR')} FCFA
                  </span>
                </div>
              </div>
            )}

            {/* Chantier */}
            {transaction?.chantier && (
              <div className="mb-6 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Chantier / Site de livraison</p>
                <p className="font-semibold">{transaction.chantier}</p>
              </div>
            )}

            {/* Photo Section for BL */}
            {isBL && !validated && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <Camera className="w-5 h-5" /> Photo de livraison (optionnel)
                </h3>
                {deliveryPhoto ? (
                  <div className="relative">
                    <img 
                      src={deliveryPhoto} 
                      alt="Photo de livraison" 
                      className="w-full max-h-64 object-cover rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setDeliveryPhoto(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={startCamera}
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      Prendre une photo
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Image className="w-5 h-5 mr-2" />
                      Choisir une image
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Ticket Section for BL */}
            {isBL && !validated && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <FileUp className="w-5 h-5" /> Ticket de livraison (optionnel)
                </h3>
                {deliveryTicket ? (
                  <div className="relative flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <File className="w-8 h-8 text-amber-600" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{ticketFileName}</p>
                      <p className="text-xs text-slate-500">Fichier joint</p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setDeliveryTicket(null);
                        setTicketFileName('');
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => ticketInputRef.current?.click()}
                  >
                    <FileUp className="w-5 h-5 mr-2" />
                    Joindre un ticket (PDF ou image)
                  </Button>
                )}
              </div>
            )}

            {/* Display existing delivery photo */}
            {isBL && validated && transaction?.delivery_photo && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <Camera className="w-5 h-5" /> Photo de livraison
                </h3>
                <img 
                  src={transaction.delivery_photo} 
                  alt="Photo de livraison" 
                  className="w-full max-h-64 object-cover rounded-lg"
                />
              </div>
            )}

            {/* Display existing delivery ticket */}
            {isBL && validated && transaction?.delivery_ticket && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <File className="w-5 h-5" /> Ticket de livraison
                </h3>
                <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                  <File className="w-8 h-8 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-medium text-sm">{transaction.delivery_ticket_name || 'ticket.pdf'}</p>
                    <p className="text-xs text-slate-500">Fichier joint</p>
                  </div>
                </div>
              </div>
            )}

            {/* Validation Status */}
            {validated && transaction?.client_validated_at && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">
                    {isBL ? 'Livraison confirmée' : 'Validé'} le {new Date(transaction.client_validated_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-4">
              {!validated && (
                <Button
                  onClick={handleValidate}
                  disabled={validating}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-6 text-lg"
                  data-testid="validate-document-btn"
                >
                  {validating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {isBL ? 'Confirmation en cours...' : 'Validation en cours...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-6 h-6 mr-2" />
                      {isBL ? 'CONFIRMER LA LIVRAISON' : 'VALIDER CE DOCUMENT'}
                    </>
                  )}
                </Button>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleDownloadPDF}
                  variant={validated ? "default" : "outline"}
                  className={`flex-1 py-4 ${validated ? 'bg-[#0C1B38] hover:bg-[#152a55] text-white' : ''}`}
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Télécharger le BL
                </Button>
                
                {/* Download complete delivery report - only for validated BL with attachments */}
                {isBL && validated && (transaction?.delivery_photo || transaction?.delivery_ticket) && (
                  <Button
                    onClick={handleDownloadDeliveryReport}
                    className="flex-1 py-4 bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="download-delivery-report-btn"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    Rapport complet
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-100 p-4 text-center text-sm text-slate-600">
            <p className="font-semibold text-[#0C1B38]">AGRABAT</p>
            <p>190 Avenue Lamine Gueye - THIES - Sénégal</p>
            <p>RCCM THS 2024 A 3436 - NINEA 011319060 1F1</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ValidationPage;
